/*
================================================================================
File: src/pages/Settings/MyInformation.js
Description: Form for updating the user's personal information.
================================================================================
*/
import React, { useState } from "react";
import Select from "react-tailwindcss-select";
import instance from "../../api/axios";
import PageContent from "../../components/common/PageContent";
import { dayCounter } from "../../utils/formatters";
import moment from "moment";

// SVG Icons
const UserIcon = () => (
  <svg
    className="w-5 h-5"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
    />
  </svg>
);

const BellIcon = () => (
  <svg
    className="w-5 h-5"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
    />
  </svg>
);

const CodeIcon = () => (
  <svg
    className="w-5 h-5"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"
    />
  </svg>
);

const MyInformation = ({ pulldata, user }) => {
  const [displayName, setDisplayName] = useState(user.app_name);
  const [contactNumber, setContactNumber] = useState(user.phone);
  const [leadReminderDays, setLeadReminderDays] = useState({
    value: user.lead_reminder,
    label: dayCounter(user.lead_reminder),
  });
  const [password, setPassword] = useState("");
  const [podioAppEmail, setPodioAppEmail] = useState(user.app_podio_email);
  const [reDoerApiKey, setReDoerApiKey] = useState(user.ghl_api);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSave = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    const formData = new FormData();
    formData.append("owner", user.app_id);
    formData.append("name", displayName);
    formData.append("number", contactNumber);
    formData.append("lead_reminder", leadReminderDays.value);
    formData.append("podio", podioAppEmail);
    formData.append("ghl_api", reDoerApiKey);
    formData.append("type", "profile");
    if (password) {
      formData.append("password", password);
    }

    try {
      await instance.post("/updateProfile.php", formData);
      setError(null);
    } catch (e) {
      if (e.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        setError("Error updating user info: " + e.response.data.message);
        console.error("Error updating user info: ", e.response.data.message);
      } else if (e.request) {
        // The request was made but no response was received
        setError("Error: No response received from server.");
        console.error("Error: ", e.request);
      } else {
        // Something happened in setting up the request that triggered an Error
        setError("Error: " + e.message);
        console.error("Error: ", e.message);
      }
    } finally {
      setIsLoading(false);
            await pulldata(user?.app_id);
    }
  };

  return (
    <PageContent
      title="Profile Settings"
      subtitle="Profile Information"
      error={error}
    >
      <div className="p-8 space-y-6">
        {/* Hero Banner */}
        <div className="bg-indigo-600 rounded-xl p-6 text-white shadow-lg">
          <div className="flex items-center gap-4 mb-4">
            <div className="p-3 bg-white/20 rounded-lg">
              <UserIcon />
            </div>
            <div>
              <h2 className="text-2xl font-bold">{user?.app_name}</h2>
              <p className="text-indigo-100 text-sm">{user?.app_email}</p>
            </div>
          </div>
          <div className="flex gap-4">
            <div className="bg-white/10 rounded-lg px-3 py-2">
              <p className="text-xs text-indigo-100">API Key</p>
              <p className="font-mono text-sm text-white">{user?.app_api_key}</p>
            </div>
            <div className="bg-white/10 rounded-lg px-3 py-2">
              <p className="text-xs text-indigo-100">Member Since</p>
              <p className="text-sm text-white">{moment.unix(user?.app_joined).format("MMM D, YYYY")}</p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSave} className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Section A: Profile Details */}
            <div className="lg:col-span-2 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm">
              <div className="flex items-center gap-2 mb-5 pb-3 border-b border-gray-100 dark:border-gray-700">
                <div className="p-2 rounded-lg bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400">
                  <UserIcon />
                </div>
                <h3 className="text-base font-semibold text-gray-800 dark:text-gray-100">Profile Details</h3>
              </div>
              <div className="space-y-5">
                <div>
                  <label
                    htmlFor="displayName"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1"
                  >
                    Display Name
                  </label>
                  <input
                    type="text"
                    id="displayName"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400"
                  />
                </div>
                <div>
                  <label
                    htmlFor="emailAddress"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1"
                  >
                    E-Mail Address
                  </label>
                  <input
                    type="email"
                    readOnly
                    id="emailAddress"
                    value={user.app_email}
                    className="mt-1 block w-full px-3 py-2 bg-gray-100 dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-gray-200 cursor-not-allowed"
                  />
                </div>
                <div>
                  <label
                    htmlFor="password"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1"
                  >
                    New Password
                  </label>
                  <input
                    autoComplete="off"
                    type="password"
                    value={password}
                    id="password"
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Leave blank to keep current password"
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400"
                  />
                </div>
                <div>
                  <label
                    htmlFor="contactNumber"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1"
                  >
                    Contact Number
                  </label>
                  <input
                    type="tel"
                    id="contactNumber"
                    value={contactNumber}
                    onChange={(e) => setContactNumber(e.target.value)}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400"
                  />
                </div>
              </div>
            </div>

            {/* Section B: Lead Reminders */}
            <div className="flexbg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm">
              <div className="flex items-center gap-2 mb-5 pb-3 border-b border-gray-100 dark:border-gray-700">
                <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400">
                  <BellIcon />
                </div>
                <h3 className="text-base font-semibold text-gray-800 dark:text-gray-100">Lead Reminders</h3>
              </div>
              <div>
                <label
                  htmlFor="leadReminderDays"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2"
                >
                  Set Lead Reminder Days
                </label>
                <Select
                  classNames={{
    menuButton: ({ isDisabled }) =>
      `flex text-sm text-gray-700 border border-gray-300 rounded shadow-sm transition-all duration-300 focus:outline-none bg-white hover:border-gray-400 focus:border-indigo-500 focus:ring focus:ring-indigo-500/20 dark:bg-gray-900 dark:border-gray-600 dark:text-gray-200 ${
        isDisabled ? "bg-gray-200" : ""
      }`,
    menu: "absolute z-10 w-full bg-white shadow-lg border rounded py-1 mt-1.5 text-sm text-gray-700 dark:bg-gray-900 dark:border-gray-600 dark:text-gray-200",
    listItem: ({ isSelected }) =>
      `block transition duration-200 px-2 py-2 cursor-pointer select-none truncate rounded ${
        isSelected
          ? "text-white bg-indigo-500"
          : "text-gray-500 hover:bg-indigo-100 hover:text-indigo-500 dark:text-gray-200 dark:hover:bg-gray-700 dark:hover:text-white"
      }`,
  }}
                value={leadReminderDays}
                onChange={(value) => setLeadReminderDays(value)}
                options={[
                  { value: "86400", label: "1 Day" },
                  { value: "172800", label: "2 Days" },
                  { value: "259200", label: "3 Days" },
                  { value: "345600", label: "4 Days" },
                  { value: "432000", label: "5 Days" },
                  { value: "604800", label: "1 Week" },
                  { value: "2592000", label: "1 Month" },
                ]}
                />
              </div>
            </div>
          </div>

          {/* Section C: Integrations */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm">
            <div className="flex items-center gap-2 mb-5 pb-3 border-b border-gray-100 dark:border-gray-700">
              <div className="p-2 rounded-lg bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400">
                <CodeIcon />
              </div>
              <h3 className="text-base font-semibold text-gray-800 dark:text-gray-100">Integrations</h3>
            </div>
            <div className="space-y-5">
              <div>
                <label
                  htmlFor="podioAppEmail"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2"
                >
                  Podio App Email
                </label>
                <textarea
                  value={podioAppEmail}
                  onChange={(e) => setPodioAppEmail(e.target.value)}
                  id="podioAppEmail"
                  rows="3"
                  className="block p-3 w-full text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-purple-500 dark:focus:ring-purple-400"
                />
              </div>
              <div>
                <label
                  htmlFor="reDoerApiKey"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2"
                >
                  ReDoer API Key
                </label>
                <textarea
                  value={reDoerApiKey}
                  onChange={(e) => setReDoerApiKey(e.target.value)}
                  id="reDoerApiKey"
                  rows="3"
                  className="block p-3 w-full text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
                />
              </div>
            </div>
          </div>

          {/* Save Button */}
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={isLoading}
              className="px-5 py-2 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isLoading ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </form>
      </div>
    </PageContent>
  );
};

export default MyInformation;
